import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { AdminSDK, UserFlagUpdate } from '../../lib/admin';
import { User, Expense } from '../../types/models';
import LoadingSpinner from '../../components/LoadingSpinner';
import { trackError, createErrorMeta } from '../../lib/telemetry';

interface UserDetail extends User {
  flags: any;
}

interface ReceiptPreview {
  url: string;
  filename: string;
  size: number;
  uploadedAt: Date;
}

const UserDetail: React.FC = () => {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [receipts, setReceipts] = useState<ReceiptPreview[]>([]);
  const [showPreview, setShowPreview] = useState<string | null>(null);
  const [legalHoldForm, setLegalHoldForm] = useState({
    enabled: false,
    scope: 'ALL' as 'ALL' | 'YEAR',
    year: new Date().getFullYear(),
    note: '',
  });

  useEffect(() => {
    if (uid) {
      loadUserData();
    }
  }, [uid]);

  const loadUserData = async () => {
    if (!uid) return;

    try {
      setLoading(true);
      setError(null);

      // Load user data
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (!userDoc.exists()) {
        setError('User not found');
        return;
      }

      const userData = { uid, ...userDoc.data() } as UserDetail;
      setUser(userData);

      // Load recent receipts
      await loadReceipts();
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load user data';
      setError(errorMessage);

      trackError(
        'Admin user detail load failed',
        createErrorMeta('ui', 'ADMIN_USER_DETAIL_LOAD', 'S3', 'degraded', '24h', {
          userAction: 'admin_user_detail_view',
          extras: { targetUser: uid },
        }),
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const loadReceipts = async () => {
    if (!uid) return;

    try {
      // Get expenses with receipts (most recent first)
      const expensesRef = collection(db, 'users', uid, 'expenses');
      const expensesQuery = query(expensesRef, orderBy('uploadedAt', 'desc'), limit(20));
      const expensesSnapshot = await getDocs(expensesQuery);

      const receiptsData: ReceiptPreview[] = [];

      for (const expenseDoc of expensesSnapshot.docs) {
        const expense = expenseDoc.data() as Expense;
        if (expense.receiptPhotoURL) {
          // Extract filename from URL
          const filename = expense.receiptPhotoURL.split('/').pop() || 'unknown';

          receiptsData.push({
            url: expense.receiptPhotoURL,
            filename,
            size: 0, // We'll get this from signed URL if needed
            uploadedAt:
              expense.uploadedAt instanceof Date
                ? expense.uploadedAt
                : (expense.uploadedAt as any).toDate(),
          });
        }
      }

      setReceipts(receiptsData);
    } catch (error) {
      console.warn('Failed to load receipts:', error);
    }
  };

  const handleHoldToggle = async () => {
    if (!user) return;

    try {
      setActionLoading('hold');
      const update: UserFlagUpdate = {
        uid: user.uid,
        onHold: !user.flags?.onHold,
      };

      await AdminSDK.setUserFlag(update);

      // Refresh user data
      await loadUserData();
    } catch (error: any) {
      trackError(
        'Admin hold toggle failed',
        createErrorMeta('ui', 'ADMIN_HOLD_TOGGLE', 'S2', 'user-blocking', 'immediate', {
          userAction: 'admin_toggle_hold',
          extras: { targetUser: user.uid },
        }),
        error
      );
      alert('Failed to update hold status: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleLegalHoldSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setActionLoading('legal-hold');
      const update: UserFlagUpdate = {
        uid: user.uid,
        legalHold: legalHoldForm,
      };

      await AdminSDK.setUserFlag(update);

      // Reset form and refresh user data
      setLegalHoldForm({
        enabled: false,
        scope: 'ALL',
        year: new Date().getFullYear(),
        note: '',
      });
      await loadUserData();
    } catch (error: any) {
      trackError(
        'Admin legal hold failed',
        createErrorMeta('ui', 'ADMIN_LEGAL_HOLD', 'S2', 'user-blocking', 'immediate', {
          userAction: 'admin_legal_hold',
          extras: { targetUser: user.uid },
        }),
        error
      );
      alert('Failed to update legal hold: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReceiptPreview = async (receipt: ReceiptPreview) => {
    try {
      const receiptPath = `receipts/${user!.uid}/${receipt.filename}`;
      const result = await AdminSDK.getSignedReceiptUrl(user!.uid, receiptPath);
      setShowPreview(result.signedUrl);
    } catch (error: any) {
      trackError(
        'Admin receipt preview failed',
        createErrorMeta('ui', 'ADMIN_RECEIPT_PREVIEW', 'S3', 'degraded', '24h', {
          userAction: 'admin_preview_receipt',
          extras: { targetUser: user?.uid },
        }),
        error
      );
      alert('Failed to generate preview: ' + error.message);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Loading user details..." />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-medium text-red-800 mb-2">Error Loading User</h2>
          <p className="text-red-700">{error || 'User not found'}</p>
          <button
            onClick={() => navigate('/admin')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Back to Admin Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin')}
          className="text-blue-600 hover:text-blue-800 mb-4 inline-flex items-center"
        >
          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Admin Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900">User Details</h1>
        <p className="mt-2 text-gray-600">{user.email}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* User Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">UID</label>
                <p className="text-sm text-gray-900 font-mono">{user.uid}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Display Name</label>
                <p className="text-sm text-gray-900">{user.displayName || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Email</label>
                <p className="text-sm text-gray-900">{user.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Province</label>
                <p className="text-sm text-gray-900">{user.province}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">GST Registered</label>
                <p className="text-sm text-gray-900">{user.gstRegistered ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Created</label>
                <p className="text-sm text-gray-900">{formatDate(user.createdAt.toDate())}</p>
              </div>
            </div>
          </div>

          {/* Account Flags */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Status</h2>

            {/* Hold Status */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Account Hold</h3>
                  <p className="text-sm text-gray-600">
                    {user.flags?.onHold ? 'Account is currently on hold' : 'Account is active'}
                  </p>
                </div>
                <button
                  onClick={handleHoldToggle}
                  disabled={actionLoading === 'hold'}
                  className={`px-4 py-2 rounded-md font-medium ${
                    user.flags?.onHold
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  } disabled:opacity-50`}
                >
                  {actionLoading === 'hold'
                    ? 'Updating...'
                    : user.flags?.onHold
                      ? 'Clear Hold'
                      : 'Put on Hold'}
                </button>
              </div>

              {user.flags?.onHold && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-red-800 mb-2">Hold Details</h4>
                  <div className="text-sm text-red-700 space-y-1">
                    <p>
                      <strong>Reason:</strong> {user.flags.reason || 'N/A'}
                    </p>
                    <p>
                      <strong>Since:</strong>{' '}
                      {user.flags.at ? formatDate(user.flags.at.toDate()) : 'N/A'}
                    </p>
                    {user.flags.threshold && (
                      <p>
                        <strong>Threshold:</strong> {formatBytes(user.flags.threshold.bytes || 0)}{' '}
                        uploaded, {user.flags.threshold.count || 0} files
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Legal Hold */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Legal Hold</h3>

              {user.flags?.legalHold?.enabled ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-medium text-yellow-800 mb-2">Active Legal Hold</h4>
                  <div className="text-sm text-yellow-700 space-y-1">
                    <p>
                      <strong>Scope:</strong>{' '}
                      {user.flags.legalHold.scope === 'ALL'
                        ? 'All Data'
                        : `Tax Year ${user.flags.legalHold.year}`}
                    </p>
                    <p>
                      <strong>Note:</strong> {user.flags.legalHold.note || 'N/A'}
                    </p>
                    <p>
                      <strong>Set:</strong> {formatDate(user.flags.legalHold.setAt.toDate())}
                    </p>
                  </div>
                  <button
                    onClick={() => setLegalHoldForm(prev => ({ ...prev, enabled: false }))}
                    className="mt-3 px-3 py-1 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700"
                  >
                    Release Hold
                  </button>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600">No active legal hold</p>
                </div>
              )}

              <form onSubmit={handleLegalHoldSubmit} className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="legalHoldEnabled"
                    checked={legalHoldForm.enabled}
                    onChange={e =>
                      setLegalHoldForm(prev => ({ ...prev, enabled: e.target.checked }))
                    }
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <label htmlFor="legalHoldEnabled" className="ml-2 text-sm text-gray-900">
                    Enable Legal Hold
                  </label>
                </div>

                {legalHoldForm.enabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Scope</label>
                      <select
                        value={legalHoldForm.scope}
                        onChange={e =>
                          setLegalHoldForm(prev => ({
                            ...prev,
                            scope: e.target.value as 'ALL' | 'YEAR',
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      >
                        <option value="ALL">All Data</option>
                        <option value="YEAR">Specific Tax Year</option>
                      </select>
                    </div>

                    {legalHoldForm.scope === 'YEAR' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tax Year
                        </label>
                        <input
                          type="number"
                          value={legalHoldForm.year}
                          onChange={e =>
                            setLegalHoldForm(prev => ({ ...prev, year: parseInt(e.target.value) }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Note (Optional)
                      </label>
                      <textarea
                        value={legalHoldForm.note}
                        onChange={e =>
                          setLegalHoldForm(prev => ({ ...prev, note: e.target.value }))
                        }
                        placeholder="Reason for legal hold..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        rows={3}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={actionLoading === 'legal-hold'}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      {actionLoading === 'legal-hold' ? 'Updating...' : 'Apply Legal Hold'}
                    </button>
                  </>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Receipts Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Receipts</h2>

            {receipts.length === 0 ? (
              <p className="text-gray-500 text-sm">No receipts found</p>
            ) : (
              <div className="space-y-3">
                {receipts.slice(0, 10).map((receipt, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {receipt.filename}
                      </p>
                      <p className="text-xs text-gray-500">{formatDate(receipt.uploadedAt)}</p>
                    </div>
                    <button
                      onClick={() => handleReceiptPreview(receipt)}
                      className="ml-3 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Preview
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Receipt Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Receipt Preview</h3>
              <button
                onClick={() => setShowPreview(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <img
                src={showPreview}
                alt="Receipt preview"
                className="max-w-full max-h-[70vh] object-contain mx-auto"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetail;
